import React, { useState } from 'react';
import { Box, Typography, Button, Stack, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useFirebaseAuthentication } from 'lib/firebase';
import { useShoppingList } from './useShoppingList';
import ActiveGroceryList from './ActiveGroceryList';
import HistoryGroceryList from './HistoryGroceryList';
import GroceryExportModal from './GroceryExportModal';
import IosShareIcon from '@mui/icons-material/IosShare';

const ShoppingListPage = ({ 
    title = 'Grocery Request List', 
    collectionName = 'groceryItems',
    validationType = 'grocery',
    themeColor = '#ed8936'
}) => {
    const navigate = useNavigate();
    const authUser = useFirebaseAuthentication();
    const [viewMode, setViewMode] = useState('active'); // 'active' or 'history'
    const [showExportModal, setShowExportModal] = useState(false);

    const {
        activeItems,
        historyItems,
        loading,
        historyLoading,
        hasMoreHistory,
        addItem,
        checkItem,
        uncheckItem,
        deleteItem,
        loadMoreHistory
    } = useShoppingList(collectionName);

    const handleBack = () => {
        navigate('/family-central');
    };

    const handleVoiceTranscription = async (transcription, userId) => {
        if (transcription && transcription.trim()) {
            try {
                await addItem(transcription, userId);
            } catch (error) {
                console.error('Error adding item from voice:', error);
            }
        }
    };

    const handleAddManualItem = async (itemText, userId) => {
        try {
            await addItem(itemText, userId);
        } catch (error) {
            console.error('Error adding manual item:', error);
        }
    };

    const handleCheckItem = async (itemId, userId) => {
        try {
            await checkItem(itemId, userId);
        } catch (error) {
            console.error('Error checking item:', error);
        }
    };

    const handleUncheckItem = async (itemId) => {
        try {
            await uncheckItem(itemId);
        } catch (error) {
            console.error('Error unchecking item:', error);
        }
    };

    const handleDeleteItem = async (itemId) => {
        try {
            await deleteItem(itemId);
        } catch (error) {
            console.error('Error deleting item:', error);
        }
    };

    // Get current user ID from auth
    const currentUserId = authUser?.uid || 'unknown';

    return (
        <>
            <Helmet>
                <title>{title}</title>
            </Helmet>
            
            <Box
                sx={{
                    height: '100vh',
                    width: '100vw',
                    overflow: 'auto',
                    p: { xs: 2, md: 4 },
                    bgcolor: themeColor
                }}
            >
                {/* Header */}
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <IconButton
                        onClick={handleBack}
                        size="large"
                        sx={{
                            p: 2,
                            bgcolor: 'white',
                            boxShadow: 2,
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
                        }}
                    >
                        <ArrowBackIcon fontSize="large" sx={{ color: themeColor }} />
                    </IconButton>
                    
                    <Typography
                        variant="h3"
                        fontWeight="900"
                        sx={{
                            flex: 1,
                            color: 'white',
                            fontSize: { xs: '1.75rem', md: '2.5rem' },
                            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                    >
                        {title}
                    </Typography>

                    {/* View Mode Toggle */}
                    <Stack direction="row" spacing={1} sx={{ mr: 2 }}>
                        <Button
                            variant={viewMode === 'active' ? 'contained' : 'text'}
                            onClick={() => setViewMode('active')}
                            sx={{
                                bgcolor: viewMode === 'active' ? 'white' : 'transparent',
                                color: viewMode === 'active' ? themeColor : 'white',
                                fontWeight: 900,
                                fontSize: '1.1rem',
                                px: 3,
                                py: 1.5,
                                '&:hover': {
                                    bgcolor: viewMode === 'active' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            NEED
                        </Button>
                        <Button
                            variant={viewMode === 'history' ? 'contained' : 'text'}
                            onClick={() => setViewMode('history')}
                            sx={{
                                bgcolor: viewMode === 'history' ? 'white' : 'transparent',
                                color: viewMode === 'history' ? themeColor : 'white',
                                fontWeight: 900,
                                fontSize: '1.1rem',
                                px: 3,
                                py: 1.5,
                                '&:hover': {
                                    bgcolor: viewMode === 'history' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            PURCHASED
                        </Button>
                    </Stack>

                    {/* Export Button */}
                    <IconButton
                        onClick={() => setShowExportModal(true)}
                        sx={{
                            bgcolor: 'rgba(0,0,0,0.2)', // Semi-transparent black for contrast on any color
                            color: 'white',
                            borderRadius: 2, // Rounded square
                            width: 56,
                            height: 56,
                            '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.3)'
                            }
                        }}
                    >
                        <IosShareIcon fontSize="large" />
                    </IconButton>
                </Stack>

                {/* Main Content */}
                <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
                    {/* Active or History List */}
                    {viewMode === 'active' ? (
                        <ActiveGroceryList
                            items={activeItems}
                            loading={loading}
                            onCheck={handleCheckItem}
                            onDelete={handleDeleteItem}
                            onAddItem={handleAddManualItem}
                            onVoiceTranscription={handleVoiceTranscription}
                            currentUserId={currentUserId}
                            validationType={validationType}
                        />
                    ) : (
                        <HistoryGroceryList
                            items={historyItems}
                            loading={historyLoading}
                            hasMore={hasMoreHistory}
                            onUncheck={handleUncheckItem}
                            onLoadMore={loadMoreHistory}
                            onAddItem={handleAddManualItem}
                            currentUserId={currentUserId}
                        />
                    )}
                </Box>
            </Box>

            <GroceryExportModal 
                open={showExportModal}
                onClose={() => setShowExportModal(false)}
                items={activeItems}
                title={title}
            />
        </>
    );
};

export default ShoppingListPage;
